import { useState, useEffect, useCallback, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Target,
  BarChart4,
  FileText,
  AlertTriangle,
  TrendingUp,
  Award
} from 'lucide-react'

// Import modular components
import ObjectivesKPIsView from '../ObjectivesKPIsView'
import EvidencePortfolio from '../EvidencePortfolio'

// Import types
import { EnhancedEvidenceEntry, JITCheckIn, KPAData } from '@/types/evidence'
import { CollaborationSession } from '@/types/objectives'

// Import enterprise progress calculation
import { calculateProgress } from '@/utils/kpiProgress'
import type { MeasurementDirection, MeasurementMode } from '@/types/kpi'

interface MemberKPAPanelProps {
  kpa: KPAData
  memberId: string
  memberName: string
  currentUserId?: string // The logged-in user's ID (for permission checks)
  userRole?: 'employee' | 'manager' // Determines evidence source
  reviewPeriod?: 'MIDYEAR' | 'YEAREND' // Filter measurement history by review period
  /**
   * Simple ↔ Detailed presentation. Simple (default for employees) hides the
   * RAG risk badge, the 4 stat cards, and the description/risk language so the
   * panel reads as a journal page rather than a metrics dashboard. Detailed
   * restores the full enterprise view.
   */
  journeyViewMode?: 'simple' | 'detailed'
  
  // Filtered data for this specific KPA
  evidence: EnhancedEvidenceEntry[]
  
  // Evidence handlers
  checkEvidenceEditable?: (evidence: EnhancedEvidenceEntry) => { canEdit: boolean; reason?: string }
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined> | void
  onUpdateEvidence?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void
  onUpdateEvidenceClassification?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => void
  onRefreshEvidence?: () => Promise<void>
  onDeleteEvidence: (evidenceId: string) => void
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  
  // KPI handlers
  onViewKPIDetails: (kpi: any) => void
  onUpdateKPIMeasurement?: (kpiId: string, value: number, notes?: string) => void
  onLinkToObjective?: (kpiId: string, objectiveId: number) => void
  
  // Individual Objectives handlers
  onCreateObjective?: (objective: any) => void
  onUpdateObjective?: (objectiveId: number, updates: any) => void
  onDeleteObjective?: (objectiveId: number) => void
  onViewObjectiveDetails?: (objective: any) => void
  onApproveObjective?: (objectiveId: number) => void
  onRejectObjective?: (objectiveId: number, reason: string) => void
  
  // Manager-specific actions
  onRateKPA?: (kpaId: string, rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr') => void
  onAddKPANotes?: (kpaId: string, notes: string) => void
  
  // Objective configuration - opens approval dialog
  onOpenObjectiveConfig?: (initialTab?: 'pending' | 'active' | 'rejected' | 'allocate') => void
  
  // Navigation support
  highlightedKPIId?: string | null
  
  // Sticky offset for sub-tabs (measured from parent tab bar)
  stickyTopOffset?: number
}

export default function MemberKPAPanel({
  kpa,
  memberId,
  memberName,
  currentUserId,
  evidence,
  userRole = 'manager', // Default to manager for backward compatibility
  reviewPeriod,
  journeyViewMode = 'simple',
  checkEvidenceEditable,
  onAddEvidence,
  onUpdateEvidence,
  onUpdateEvidenceClassification,
  onRefreshEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onViewKPIDetails,
  onUpdateKPIMeasurement,
  onLinkToObjective,
  onCreateObjective,
  onUpdateObjective,
  onDeleteObjective,
  onViewObjectiveDetails,
  onApproveObjective,
  onRejectObjective,
  onRateKPA,
  onOpenObjectiveConfig,
  highlightedKPIId,
  stickyTopOffset = 112
  // onAddKPANotes - reserved for future use
}: MemberKPAPanelProps) {
  // Acknowledge unused props for future functionality
  void onViewKPIDetails
  void onUpdateKPIMeasurement
  void onLinkToObjective
  void onCreateObjective
  void onUpdateObjective
  void onDeleteObjective
  void onViewObjectiveDetails
  void onApproveObjective
  void onRejectObjective
  
  // Persist sub-tab selection using sessionStorage
  const subTabStorageKey = `kpaPanel_${memberId}_${kpa.id}_activeSubTab`
  const [activeSubTab, setActiveSubTab] = useState(() => {
    const stored = sessionStorage.getItem(subTabStorageKey)
    return stored || 'objectives-kpis'
  })
  
  // Wrap setter to persist to sessionStorage
  const handleSubTabChange = useCallback((newTab: string) => {
    setActiveSubTab(newTab)
    sessionStorage.setItem(subTabStorageKey, newTab)
  }, [subTabStorageKey])
  
  // Persist evidence modal state to survive parent re-renders
  const evidenceModalStorageKey = `kpaPanel_${memberId}_${kpa.id}_evidenceModalOpen`
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(() => {
    const stored = sessionStorage.getItem(evidenceModalStorageKey)
    return stored === 'true'
  })
  
  // Wrap setter to persist modal state
  const handleSetEvidenceModalOpen = useCallback((open: boolean) => {
    setEvidenceModalOpen(open)
    sessionStorage.setItem(evidenceModalStorageKey, open.toString())
    if (!open) {
      // Clear selected KPI when closing modal
      sessionStorage.removeItem(`kpaPanel_${memberId}_${kpa.id}_selectedKPI`)
    }
  }, [evidenceModalStorageKey, memberId, kpa.id])
  
  // Persist selected KPI to survive re-renders
  const selectedKPIStorageKey = `kpaPanel_${memberId}_${kpa.id}_selectedKPI`
  const [selectedKPI, setSelectedKPI] = useState<{ id: string; name: string; objectiveId: number } | null>(() => {
    const stored = sessionStorage.getItem(selectedKPIStorageKey)
    return stored ? JSON.parse(stored) : null
  })
  
  // Wrap setter to persist selected KPI
  const handleSetSelectedKPI = useCallback((kpi: { id: string; name: string; objectiveId: number } | null) => {
    setSelectedKPI(kpi)
    if (kpi) {
      sessionStorage.setItem(selectedKPIStorageKey, JSON.stringify(kpi))
    } else {
      sessionStorage.removeItem(selectedKPIStorageKey)
    }
  }, [selectedKPIStorageKey])
  
  // Responsive sticky offset: 61px on mobile (dropdown height), dynamic on desktop (measured tab bar)
  const [isMdScreen, setIsMdScreen] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)')
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => setIsMdScreen(e.matches)
    handleChange(mql)
    mql.addEventListener('change', handleChange)
    return () => mql.removeEventListener('change', handleChange)
  }, [])
  const stickyTop = isMdScreen ? stickyTopOffset : 61
  
  // Debug logging
  if (import.meta.env.DEV) console.log(`📊 MemberKPAPanel for "${kpa.name}": received ${evidence.length} evidence items`)
  
  // Filter out rejected objectives
  const activeObjectives = kpa.individualObjectives.filter(obj => obj.status !== 'rejected')
  
  // Calculate dynamic KPA progress from objectives
  const calculateKPAProgress = () => {
    if (!activeObjectives || activeObjectives.length === 0) {
      return kpa.averageProgress || 0
    }
    
    // Calculate each objective's progress (based on its KPIs)
    const objectiveProgresses: number[] = []
    const totalProgress = activeObjectives.reduce((sum, objective) => {
      if (!objective.kpis || objective.kpis.length === 0) {
        const prog = objective.progress || 0
        objectiveProgresses.push(prog)
        return sum + prog
      }
      
      // Calculate objective progress from KPIs using enterprise calculation
      const kpiProgresses: number[] = []
      const objectiveProgress = objective.kpis.reduce((kpiSum, kpi) => {
        // Use period-specific value based on reviewPeriod prop
        const periodValue = reviewPeriod === 'MIDYEAR' 
          ? ((kpi as any).midyear_value ?? kpi.current_value)
          : ((kpi as any).yearend_value ?? kpi.current_value)
        
        // Use enterprise progress calculation
        const mode = ((kpi as any).measurement_mode || 'numeric') as MeasurementMode
        const direction = ((kpi as any).measurement_direction || 'higher_is_better') as MeasurementDirection
        
        // Debug log for Turnaround time KPI
        if (kpi.name?.includes('Turnaround') || kpi.name?.includes('turnaround')) {
          if (import.meta.env.DEV) console.log(`🔍 Turnaround KPI Debug:`, {
            name: kpi.name,
            periodValue,
            target: kpi.target_value,
            direction,
            mode,
            kpiObject: kpi
          })
        }
        
        const progressResult = calculateProgress(
          periodValue,
          kpi.target_value,
          direction,
          mode
        )
        
        if (kpi.name?.includes('Turnaround') || kpi.name?.includes('turnaround')) {
          if (import.meta.env.DEV) console.log(`🔍 Turnaround Progress Result:`, progressResult)
        }
        
        kpiProgresses.push(progressResult.progress)
        return kpiSum + progressResult.progress
      }, 0) / objective.kpis.length
      
      objectiveProgresses.push(objectiveProgress)
      if (import.meta.env.DEV) console.log(`📊 MemberKPAPanel KPA "${kpa.name}" Objective "${objective.title}": KPIs=[${kpiProgresses.join(', ')}], Avg=${objectiveProgress.toFixed(1)}%`)
      return sum + objectiveProgress
    }, 0)
    
    const avgProgress = Math.round(totalProgress / activeObjectives.length)
    if (import.meta.env.DEV) console.log(`📊 MemberKPAPanel KPA "${kpa.name}" Total: Objectives=[${objectiveProgresses.map(p => p.toFixed(1)).join(', ')}], Avg=${avgProgress}%`)
    return avgProgress
  }
  
  const dynamicKPAProgress = calculateKPAProgress()
  
  // Auto-navigate to combined Objectives & KPIs tab when a KPI is highlighted
  useEffect(() => {
    if (highlightedKPIId) {
      handleSubTabChange('objectives-kpis')
    }
  }, [highlightedKPIId, handleSubTabChange])
  
  // Handle KPI evidence actions - Opens full evidence modal
  const handleAddKPIEvidence = useCallback((kpiId: string, kpiName: string, objectiveId: number) => {
    handleSetSelectedKPI({ id: kpiId, name: kpiName, objectiveId })
    handleSetEvidenceModalOpen(true)
  }, [handleSetEvidenceModalOpen, handleSetSelectedKPI])
  
  
  // Handle KPI-specific evidence submission with context
  const handleAddKPISpecificEvidence = useCallback(async (evidenceData: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>): Promise<EnhancedEvidenceEntry | undefined> => {
    if (selectedKPI) {
      if (import.meta.env.DEV) console.log('📝 MemberKPAPanel: Adding evidence, keeping dialog open')
      // Inject KPI context into the evidence
      const result = await onAddEvidence({
        ...evidenceData,
        kpiId: selectedKPI.id,
        objectiveId: evidenceData.objectiveId || selectedKPI.objectiveId.toString(),
        kpaId: evidenceData.kpaId || kpa.id
      })
      if (import.meta.env.DEV) console.log('✅ MemberKPAPanel: Evidence added, evidenceModalOpen should still be true:', evidenceModalOpen)
      // DO NOT close the modal - let the user add more evidence
      return result as EnhancedEvidenceEntry | undefined
    }
    return undefined
  }, [selectedKPI, kpa.id, onAddEvidence, evidenceModalOpen])
  
  // Calculate KPA-specific metrics
  const needsApproval = activeObjectives.filter(obj => obj.status === 'pending_approval' || obj.status === 'pending-approval')
  
  // Extract all KPIs from objectives and underlying KPIs for evidence linking
  const availableKPIs = useMemo(() => {
    const kpis: Array<{
      id: string
      name: string
      kpaId?: string
      objectiveTitle?: string
      current_value?: number
      target_value?: number
      unit?: string
      measurement_history?: string | null
    }> = []
    
    // Add KPIs from individual objectives (only active objectives)
    activeObjectives.forEach(obj => {
      if (obj.kpis && obj.kpis.length > 0) {
        obj.kpis.forEach(kpi => {
          // Use period-specific value based on reviewPeriod
          const periodValue = reviewPeriod === 'MIDYEAR' 
            ? ((kpi as any).midyear_value ?? kpi.current_value)
            : ((kpi as any).yearend_value ?? kpi.current_value)
          kpis.push({
            id: kpi.id,
            name: kpi.name,
            kpaId: kpa.id,
            objectiveTitle: obj.title,
            current_value: periodValue,
            target_value: kpi.target_value,
            unit: kpi.unit,
            measurement_history: (kpi as any).measurement_history
          })
        })
      }
    })
    
    // Add underlying KPIs from the KPA
    if (kpa.underlyingKPIs && kpa.underlyingKPIs.length > 0) {
      kpa.underlyingKPIs.forEach(kpi => {
        // Only add if not already in the list
        if (!kpis.find(k => k.id === kpi.id)) {
          // Use period-specific value based on reviewPeriod
          const periodValue = reviewPeriod === 'MIDYEAR' 
            ? ((kpi as any).midyear_value ?? kpi.current_value)
            : ((kpi as any).yearend_value ?? kpi.current_value)
          kpis.push({
            id: kpi.id,
            name: kpi.name,
            kpaId: kpa.id,
            current_value: periodValue ?? undefined,
            target_value: kpi.target_value,
            unit: kpi.unit,
            measurement_history: (kpi as any).measurement_history
          })
        }
      })
    }
    
    return kpis
  }, [activeObjectives, kpa.underlyingKPIs, reviewPeriod])
  
  return (
    <div className="flex flex-col">
      {/* KPA Header — Quarterly editorial chapter opener in Simple, full
          enterprise gradient + metric grid in Detailed. */}
      <div className={`border-b ${journeyViewMode === 'detailed'
        ? 'p-4 md:p-6 bg-gradient-to-br from-heritage-blue/10 to-future-blue/10'
        : 'bg-quarterly-bone px-6 sm:px-10 md:px-16 lg:px-24 py-10 md:py-14 border-quarterly-rule'}`}>
        {journeyViewMode === 'simple' ? (
          // Magazine chapter opener: dateline → serif H1 → italic standfirst →
          // tobacco hairline rule. No status pills, no metric cards. The
          // surrounding sub-tabs sit on the same bone surface.
          <div className="max-w-[720px] mx-auto">
            {/* "Next reflection" dateline removed — there is no real per-KPA
                review schedule to drive it (it was previously a synthesised
                today+Nd value), and the team member's actual check-in dates
                already live on the my Check-Ins tab. */}
            <h2 className="font-serif text-quarterly-ink text-[36px] md:text-[44px] leading-[1.08] tracking-tight text-balance mb-4">
              {kpa.display_name || kpa.name}
            </h2>
            {kpa.description && (
              <p className="font-editorial italic text-quarterly-ink-muted text-[18px] md:text-[20px] leading-[1.55] max-w-[600px]">
                {kpa.description}
              </p>
            )}
            {needsApproval.length > 0 && (
              <button
                onClick={() => onOpenObjectiveConfig?.('pending')}
                className="mt-5 inline-flex items-center gap-2 text-[12px] font-medium tracking-[0.18em] uppercase text-quarterly-tobacco hover:underline"
              >
                {needsApproval.length} {userRole === 'employee' ? `objective${needsApproval.length === 1 ? '' : 's'} pending approval` : `objective${needsApproval.length === 1 ? '' : 's'} to approve`}
                <span>→</span>
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                <h2 className="text-xl md:text-2xl font-bold text-heritage-blue">
                  {kpa.display_name || kpa.name}
                </h2>
              </div>
            {journeyViewMode === 'detailed' && (
              <p className="text-gray-600 text-sm md:text-base mb-4">{kpa.description}</p>
            )}

            {/* KPA Metrics Grid — Detailed only. Simple mode keeps the page
                journal-like and skips the metric cards entirely. */}
            {journeyViewMode === 'detailed' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                <Card className="border-heritage-blue/20">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-heritage-blue" />
                      <div>
                        <p className="text-xs text-gray-600">Progress</p>
                        <p className="text-lg font-bold text-heritage-blue">{dynamicKPAProgress}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-green-200">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-600">Objectives</p>
                        <p className="text-lg font-bold text-green-600">{activeObjectives.length}</p>
                        {needsApproval.length > 0 && (
                          <p className="text-sm text-gray-600">{needsApproval.length} pending approval</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-200">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <BarChart4 className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-600">KPIs</p>
                        <p className="text-lg font-bold text-blue-600">{kpa.underlyingKPIs.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-purple-200">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      <div>
                        <p className="text-xs text-gray-600">Impact Items</p>
                        <p className="text-lg font-bold text-purple-600">{evidence.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Status Indicators */}
            <div className={`flex flex-wrap items-center gap-3 md:gap-4 ${journeyViewMode === 'detailed' ? 'mt-3' : 'mt-1'}`}>
              {needsApproval.length > 0 && (
                <button 
                  onClick={() => onOpenObjectiveConfig?.('pending')}
                  className="flex items-center gap-2 text-sm px-3 py-1.5 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors cursor-pointer group"
                >
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  <span className="text-orange-700 font-medium">
                    {needsApproval.length} {userRole === 'employee' ? 'objectives pending approval' : 'objectives need approval'}
                  </span>
                  <span className="text-orange-500 text-xs group-hover:underline">
                    → {userRole === 'employee' ? 'View' : 'Review'}
                  </span>
                </button>
              )}
              
              {/* "Next review" date removed — no real per-KPA review schedule
                  drives it; actual check-in dates live on the Check-Ins tab. */}
            </div>
          </div>
          
          {/* Manager Actions */}
          {onRateKPA && (
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRateKPA(kpa.id, 'full')}
                className="gap-2"
              >
                <Award className="h-4 w-4" />
                <span className="hidden sm:inline">Rate KPA Impact & Growth</span>
                <span className="sm:hidden">Rate</span>
              </Button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* KPA Sub-Tabs — quiet mono-tracked tab labels in Simple, full
          counts in Detailed. The (n/n) ratio is dropped from Simple
          because metric-ratios are exactly the kind of thing the editorial
          treatment is meant to escape. */}
      <div>
        <Tabs value={activeSubTab} onValueChange={handleSubTabChange} className="flex flex-col">
          {(() => {
            // Leader Observations is a PL-only surface — only show when the
            // current user is a manager viewing a team member's KPA. The tab
            // grid grows 2→3 in that case.
            const showObservationsTab = userRole === 'manager' && !!currentUserId && !!memberId && currentUserId !== memberId
            const gridColsClass = showObservationsTab ? 'grid-cols-3' : 'grid-cols-2'
            return (
          <TabsList className={`sticky z-[15] grid w-full ${gridColsClass} rounded-none border-b shadow-sm ${journeyViewMode === 'simple' ? 'bg-quarterly-bone border-quarterly-rule' : 'bg-gray-50'}`} style={{ top: `${stickyTop}px` }}>
            <TabsTrigger
              value="objectives-kpis"
              data-demo="kpa-objectives-subtab"
              className={journeyViewMode === 'simple' ? 'text-[11px] font-medium tracking-[0.22em] uppercase text-quarterly-ink-muted data-[state=active]:text-quarterly-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-quarterly-tobacco rounded-none' : ''}
            >
              {journeyViewMode === 'simple'
                ? 'Objectives & KPIs'
                : `Objectives & KPIs (${activeObjectives.length}/${kpa.underlyingKPIs.length})`}
            </TabsTrigger>
            <TabsTrigger
              value="evidence"
              data-demo="kpa-portfolio-subtab"
              className={journeyViewMode === 'simple' ? 'text-[11px] font-medium tracking-[0.22em] uppercase text-quarterly-ink-muted data-[state=active]:text-quarterly-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-quarterly-tobacco rounded-none' : ''}
            >
              {journeyViewMode === 'simple'
                ? 'Impact Portfolio'
                : `Impact Portfolio (${evidence.filter(e => e.source !== 'manager').length})`}
            </TabsTrigger>
            {showObservationsTab && (
              <TabsTrigger
                value="observations"
                className={journeyViewMode === 'simple' ? 'text-[11px] font-medium tracking-[0.22em] uppercase text-quarterly-ink-muted data-[state=active]:text-quarterly-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-quarterly-tobacco rounded-none' : ''}
              >
                {journeyViewMode === 'simple'
                  ? 'Leader Observations'
                  : `Leader Observations (${evidence.filter(e => e.source === 'manager' && e.createdBy === currentUserId).length})`}
              </TabsTrigger>
            )}
          </TabsList>
            )
          })()}
          
          <div className={journeyViewMode === 'simple' ? 'bg-quarterly-bone min-h-[400px]' : ''}>
            {/* Clean Objectives & KPIs Tab */}
            <TabsContent value="objectives-kpis" className={journeyViewMode === 'simple' ? 'm-0 px-6 sm:px-10 md:px-16 lg:px-24 py-10 md:py-12' : 'm-0 p-3 md:p-6'}>
              <ObjectivesKPIsView
                objectives={activeObjectives}
                evidence={evidence}
                userRole={userRole}
                journeyViewMode={journeyViewMode}
                onAddKPIEvidence={handleAddKPIEvidence}
              />
            </TabsContent>
            
            {/* Impact Portfolio Tab — team member's own released entries.
                Component filters out `source='manager'` rows so PL Observations
                don't bleed into the portfolio view. */}
            <TabsContent value="evidence" className="m-0 px-6 py-6">
              <EvidencePortfolio
                evidence={evidence}
                kpaId={kpa.id}
                availableKPIs={availableKPIs}
                userRole={userRole}
                currentUserId={currentUserId}
                targetUserId={memberId}
                reviewPeriod={reviewPeriod}
                checkEvidenceEditable={checkEvidenceEditable}
                showManagerCannotAddNotice={userRole === 'manager'}
                ownerDisplayName={memberName}
                onAddEvidence={onAddEvidence}
                onUpdateEvidence={onUpdateEvidence}
                onRefreshEvidence={onRefreshEvidence}
                onDeleteEvidence={onDeleteEvidence}
                onUploadDocument={onUploadDocument}
                onClassifyEvidence={onUpdateEvidenceClassification}
              />
            </TabsContent>

            {/* Leader Observations Tab — PL's own private notes about this
                team member. observationsMode=true inverts the Add permission
                (PL can add, team member cannot) and filters to manager-source
                entries authored by the current PL. Confidentiality is enforced
                server-side: team member never sees these. */}
            <TabsContent value="observations" className="m-0 px-6 py-6">
              <EvidencePortfolio
                evidence={evidence}
                // Send the NUMERIC KPA id (not the display slug) so a new
                // observation is stored with a resolvable kpa_id. Observations
                // carry no kpiId for the backend to derive the KPA from, so a slug
                // would persist as kpa_id=NULL and then be dropped by the parent's
                // per-KPA evidence filter — the entry saves but never shows.
                kpaId={(kpa as any).numericId ?? kpa.id}
                // This KPA, so each observation shows its KPA tag (HR UAT list #2).
                kpaOptions={[{ id: kpa.id, numericId: kpa.numericId, name: kpa.display_name || kpa.name }]}
                availableKPIs={availableKPIs}
                userRole={userRole}
                currentUserId={currentUserId}
                targetUserId={memberId}
                reviewPeriod={reviewPeriod}
                checkEvidenceEditable={checkEvidenceEditable}
                observationsMode={true}
                ownerDisplayName={memberName}
                onAddEvidence={onAddEvidence}
                onUpdateEvidence={onUpdateEvidence}
                onRefreshEvidence={onRefreshEvidence}
                onDeleteEvidence={onDeleteEvidence}
                onUploadDocument={onUploadDocument}
                onClassifyEvidence={onUpdateEvidenceClassification}
              />
            </TabsContent>

          </div>
        </Tabs>
      </div>
      
      {/* Full Evidence Modal for KPI Context */}
      <Dialog open={evidenceModalOpen} onOpenChange={handleSetEvidenceModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Impact Portfolio for {selectedKPI?.name}</DialogTitle>
            <DialogDescription>
              {selectedKPI && (
                <>View and manage impact portfolio entries for this KPI. Entries will be automatically linked to <strong>{selectedKPI.name}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {selectedKPI ? (
              <EvidencePortfolio
                evidence={evidence.filter(e => e.kpiId === selectedKPI.id)}
                kpaId={kpa.id}
                availableKPIs={selectedKPI ? (() => {
                  const kpi = kpa.underlyingKPIs.find(k => k.id === selectedKPI.id)
                  // Use period-specific value based on reviewPeriod
                  const periodValue = reviewPeriod === 'MIDYEAR' 
                    ? ((kpi as any)?.midyear_value ?? kpi?.current_value)
                    : ((kpi as any)?.yearend_value ?? kpi?.current_value)
                  return [{
                    id: selectedKPI.id,
                    name: selectedKPI.name,
                    objectiveTitle: kpa.individualObjectives.find(obj => obj.id === selectedKPI.objectiveId)?.title,
                    current_value: periodValue ?? undefined,
                    target_value: kpi?.target_value,
                    unit: kpi?.unit,
                    measurement_history: (kpi as any)?.measurement_history
                  }]
                })() : availableKPIs}
                preSelectedKPIId={selectedKPI.id}
                hideUploadButton={true}
                userRole={userRole}
                currentUserId={currentUserId}
                reviewPeriod={reviewPeriod}
                targetUserId={memberId}
                showManagerCannotAddNotice={userRole === 'manager'}
                ownerDisplayName={memberName}
                onAddEvidence={handleAddKPISpecificEvidence}
                onUpdateEvidence={onUpdateEvidence}
                onRefreshEvidence={onRefreshEvidence}
                onDeleteEvidence={onDeleteEvidence}
                onUploadDocument={onUploadDocument}
                onClassifyEvidence={onUpdateEvidenceClassification}
              />
            ) : null}
            {activeObjectives.length < kpa.individualObjectives.length && (
              <div className="mt-4 p-3 bg-gray-100 rounded-lg text-sm text-gray-600">
                <p>Note: {kpa.individualObjectives.length - activeObjectives.length} rejected objective(s) hidden from view</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
